import React, { useEffect, useReducer, useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';

import DatePicker from '../../common/DatePicker';
import InputWrapper from '../../common/InputWrapper';
import Select from '../../common/Select';
import Button from '../../common/Button';

import { preventSubmittingByEnter, statusOptions, suppliesStatusOptions } from '../../../helpers';

import CountragentSelect from '../../common/CountragentSelect';
import { fetchSupplies } from '../../../actions/supplies';
import { SUPPLIES_COUNT_ON_PAGE } from '../../../config';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const Wrapper = styled('form')`
  background-color: #fcfdfd;
  padding: 5px 10px;
  margin-bottom: 10px;

  .dates-block {
    display: flex;
    .react-datepicker-wrapper {
      width: calc((100% - 25px) / 2);
    }
    .date-separator {
      width: 25px;
      text-align: center;
      font-size: 22px;
      font-weight: 600;
    }
  }

  .close-icon {
    cursor: pointer;
    font-size: 20px;
  }

  .close-button.text-right.close-icon {
    width: 40px;
    border: none;
    background: transparent;
    margin: 0 0 0 auto;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const formikInitialValues = {
  status: null,
  supplierId: null,
  consignorId: null,
  dateFrom: null,
  dateTo: null,
  dateOfAcceptFrom: null,
  dateOfAcceptTo: null,
  arrivalDateFrom: null,
  arrivalDateTo: null,
};

const FilterBlock = (props) => {
  const { setShowFilter, page, handleSetFilters } = props;

  const { t } = useAutoTranslation();

  const formik = useFormik({
    initialValues: formikInitialValues,
    onSubmit: (values) => {
      const filters = {};
      if (values.status && values.status.length) {
        filters.status = values.status.map((s) => s.value).join(',');
      }
      if (values.supplierId) {
        filters.supplier_id = values.supplierId.id;
      }
      if (values.consignorId) {
        filters.consignor_id = values.consignorId.id;
      }
      if (values.dateFrom) {
        filters.date_from = moment(values.dateFrom).format('YYYY-MM-DD[T]HH:mm:ss');
      }
      if (values.dateTo) {
        filters.date_to = moment(values.dateTo).format('YYYY-MM-DD[T]HH:mm:ss');
      }
      if (values.dateOfAcceptFrom) {
        filters.date_of_accept_from = moment(values.dateOfAcceptFrom).format(
          'YYYY-MM-DD[T]HH:mm:ss',
        );
      }
      if (values.dateOfAcceptTo) {
        filters.date_of_accept_to = moment(values.dateOfAcceptTo).format('YYYY-MM-DD[T]HH:mm:ss');
      }
      if (values.arrivalDateFrom) {
        filters.arrival_date_from = moment(values.arrivalDateFrom).format('YYYY-MM-DD[T]HH:mm:ss');
      }
      if (values.arrivalDateTo) {
        filters.arrival_date_to = moment(values.arrivalDateTo).format('YYYY-MM-DD[T]HH:mm:ss');
      }

      handleSetFilters(filters);
    },
  });
  const { values, handleSubmit, setFieldValue } = formik;

  const setTimeSlotTo = (date, key) => {
    setFieldValue(key, moment(date).set('hour', 23).set('minute', 59).set('second', 59).toDate());
  };

  const setTimeSlotFrom = (date, key, additionalKey) => {
    setFieldValue(key, date);
    setFieldValue(additionalKey, null);
  };

  const handleEditCountreagent = () => {};

  const onInputChange = (id, data) => setFieldValue(id, data);

  return (
    <div className="mt-2">
      <Wrapper onKeyDown={preventSubmittingByEnter} onSubmit={handleSubmit}>
        <button className="close-button text-right close-icon" onClick={() => setShowFilter(false)}>
          <i className="fe-x" />
        </button>
        <div className="row">
          <div className="col-sm-4 mb-2">
            <Select
              isSearchable
              label={t('Статус поставки')}
              options={suppliesStatusOptions}
              isMulti
              value={values.status}
              onChange={(e) => setFieldValue('status', e)}
            />
          </div>
          <div className="col-sm-4 mb-2">
            <CountragentSelect
              label={t('Поставщик')}
              value={values.supplierId}
              onChange={(e) => setFieldValue('supplierId', e)}
              handleEditCountreagent={handleEditCountreagent}
              withoutIndicators={true}
              onInputChange={(e) => onInputChange('supplierId', e)}
            />
          </div>
          <div className="col-sm-4 mb-2">
            <CountragentSelect
              label={t('Грузоотправитель')}
              value={values.consignorId}
              onChange={(e) => setFieldValue('consignorId', e)}
              handleEditCountreagent={handleEditCountreagent}
              withoutIndicators={true}
              onInputChange={(e) => onInputChange('consignorId', e)}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-sm-4 mb-2">
            <label>{t('Дата создания')}</label>
            <div className="dates-block">
              <DatePicker
                selected={values.dateFrom}
                onChange={(data) => setTimeSlotFrom(data, 'dateFrom', 'dateTo')}
              />
              <div className="date-separator">-</div>
              <DatePicker
                selected={values.dateTo}
                minDate={values.dateFrom}
                maxDate={new Date()}
                onChange={(data) => setTimeSlotTo(data, 'dateTo')}
              />
            </div>
          </div>
          <div className="col-sm-4 mb-2">
            <label>{t('Дата закрытия (завершения приемки)')} </label>
            <div className="dates-block">
              <DatePicker
                selected={values.dateOfAcceptFrom}
                onChange={(data) => setTimeSlotFrom(data, 'dateOfAcceptFrom', 'dateOfAcceptTo')}
              />
              <div className="date-separator">-</div>
              <DatePicker
                selected={values.dateOfAcceptTo}
                minDate={values.dateOfAcceptFrom}
                maxDate={new Date()}
                onChange={(data) => setTimeSlotTo(data, 'dateOfAcceptTo')}
              />
            </div>
          </div>
          <div className="col-sm-4 mb-2">
            <label>{t('Дата фактического поступления')}</label>
            <div className="dates-block">
              <DatePicker
                selected={values.arrivalDateFrom}
                onChange={(data) => setTimeSlotFrom(data, 'arrivalDateFrom', 'arrivalDateTo')}
              />
              <div className="date-separator">-</div>
              <DatePicker
                selected={values.arrivalDateTo}
                minDate={values.arrivalDateFrom}
                maxDate={new Date()}
                onChange={(data) => setTimeSlotTo(data, 'arrivalDateTo')}
              />
            </div>
          </div>
        </div>
        <div className="row mt-2 mb-2">
          <div className="col-12">
            <Button type="submit" text={t('Найти')} />

            <Button
              type="reset"
              text={t('Сбросить фильтры')}
              style={{ marginLeft: '12px' }}
              onClick={() =>
                formik.resetForm({
                  values: formikInitialValues,
                })
              }
            />
          </div>
        </div>
      </Wrapper>
    </div>
  );
};
export default FilterBlock;
